import { FC, HTMLAttributes } from 'react';
import Pill from 'components/global/Pill';
import type { PillSize } from 'components/global/Pill';
import { LockupWrapper, ProductName } from './styles';
import { getProductMeta } from 'lib/utils/get-product-meta';

export type ProductLockupSize = PillSize;

export type ProductLockupOrientation = 'landscape' | 'portrait';

export interface ProductLockupProps extends HTMLAttributes<HTMLSpanElement> {
  /** SKU code shown inside the pill badge, e.g. "DS–01". Decorative — hidden from assistive tech. */
  sku: string;
  /** Layout: `landscape` (pill + name inline) or `portrait` (pill stacked above name). Defaults to landscape. */
  orientation?: ProductLockupOrientation;
  /** Light variant: inverse text + light pill border, for dark backgrounds. Dark is the default. */
  light?: boolean;
  /** Portrait only: centers the pill and name instead of aligning them to the start. */
  centered?: boolean;
  size?:
    | ProductLockupSize
    | {
        base: ProductLockupSize;
        tablet?: ProductLockupSize;
        desktop?: ProductLockupSize;
      };
}

const ProductLockup: FC<ProductLockupProps> = ({
  sku,
  orientation = 'landscape',
  light,
  centered,
  size = { base: 'sm', desktop: 'lg' },
  ...rest
}) => {
  const { base, tablet, desktop } =
    typeof size === 'string' ? { base: size } : size;

  const { code, name, trademark } = getProductMeta(sku);

  return (
    <LockupWrapper
      $orientation={orientation}
      $light={light}
      $centered={centered}
      $sizeBase={base}
      $sizeTablet={tablet}
      $sizeDesktop={desktop}
      {...rest}
    >
      <Pill
        size={size}
        light={light}
        registered={trademark === 'registered'}
        trademark={trademark === 'trademark'}
        aria-hidden
      >
        {code}
      </Pill>
      <ProductName $sizeBase={base} $sizeTablet={tablet} $sizeDesktop={desktop}>
        {name}
      </ProductName>
    </LockupWrapper>
  );
};

export default ProductLockup;

/**
 * HOW TO IMPLEMENT - PRODUCT LOCKUP
 * ---------------
 *
 * Branded product lockup: a SKU badge (Pill) + product name, set inline.
 * Pass only the `sku` — the product code, name, and trademark are resolved
 * internally via getProductMeta(sku). The pill is decorative (aria-hidden);
 * the product name is the accessible label.
 *
 * @example Canonical (defaults: size={ base: 'sm', desktop: 'lg' })
 * ```tsx
 * <ProductLockup sku="DS–01" />
 * ```
 *
 * @example Portrait — pill stacked above the name (hero / product-card placements)
 * ```tsx
 * <ProductLockup orientation="portrait" sku="DS–01" size="xl" />
 * ```
 *
 * @example Portrait, centered — pill and name centered on each other (grid/lineup tiles)
 * ```tsx
 * <ProductLockup orientation="portrait" centered sku="DM–02" size="xs" />
 * ```
 *
 * @example Responsive size (scales at breakpoints)
 * ```tsx
 * <ProductLockup sku="DM–02" size={{ base: 'sm', tablet: 'md', desktop: 'lg' }} />
 * ```
 *
 * @example Light — inverse text + light pill border, for dark backgrounds
 * ```tsx
 * <ProductLockup light sku="PM–02" size="md" />
 * ```
 */
